// NOTE: NSAI-4623 — when a browser client may start PostHog, and where it
// sends. Analytics runs only when BOTH a project key and a host are known:
//
//   - No key → off. Local dev and on-premise builds ship none, so nothing is
//     loaded and no request leaves the page.
//   - A key but no host → off too. The SDK's default host is PostHog Cloud,
//     so a forgotten host would send a deployment's events to a third party
//     nobody chose. Mobile and the extension already refuse this; the web
//     client follows the same rule through this function.
//
// The runtime pair wins. It is what a deploy writes into `/posthog-init.js`
// when it runs PostHog on the host (1VM, and staging / prod since NSAI-7744):
// the deploy is the first place that knows that instance's project key, and
// it must beat a key baked into the image for another PostHog. The build-time
// pair (local `.env`, an image built with one) applies when no deploy wrote a
// runtime pair.

export interface IAnalyticsRuntimeConfig {
  key?: string;
  host?: string;
}

export interface IResolveAnalyticsConfigProps {
  buildKey?: string;
  buildHost?: string;
  runtimeConfig?: IAnalyticsRuntimeConfig;
}

export interface IAnalyticsConfig {
  apiKey: string;
  host: string;
}

const clean = (value: string | undefined): string => value?.trim() ?? "";

export const resolveAnalyticsConfig = ({
  buildKey,
  buildHost,
  runtimeConfig,
}: IResolveAnalyticsConfigProps): IAnalyticsConfig | null => {
  const candidates = [
    { apiKey: clean(runtimeConfig?.key), host: clean(runtimeConfig?.host) },
    { apiKey: clean(buildKey), host: clean(buildHost) },
  ];

  // NOTE: A pair is taken whole, never mixed: a build key with a runtime host
  // would point one project's key at another deployment's PostHog.
  const complete = candidates.find(
    (candidate) => candidate.apiKey.length > 0 && candidate.host.length > 0,
  );

  return complete ?? null;
};
